import { useManagedTimeouts } from '@findigs/helpers/src/hooks/use-managed-timeouts';
import { useTransitionTasks } from '@findigs/helpers/src/hooks/use-transition-tasks';
import { useCallback, useEffect, useRef, useState } from 'react';

function computeFallback(index: number, length: number) {
  return index >= Math.floor(length / 2) ? length : -1;
}

export type UseSidebarStateProps = {
  items: unknown[];
  transitionDebounceMs: number;
  activeIndex: number;
};
export function useSidebarState({
  transitionDebounceMs,
  items,
  activeIndex,
}: UseSidebarStateProps) {
  const [state, setState] = useState({
    hovered: -1,
    fallback: -1,
    ready: false,
  });

  const hoveredRef = useRef(-1);
  const hoverOutAt = useRef(null as null | Date);

  const { pushTask, clearTasks, areTasksLocked, setTasksLocked, runNextFrame } =
    useTransitionTasks();

  const { setManagedTimeout } = useManagedTimeouts();

  // update the fallback when activeIndex changes so the animation starts
  // from the correct side
  useEffect(() => {
    if (activeIndex === -1) return;

    const fallback = computeFallback(activeIndex, items.length);

    setState((prev) => ({
      ...prev,
      fallback,
    }));
  }, [activeIndex, items.length]);

  const onHoverInside = useCallback(
    (i: number) => {
      hoveredRef.current = i;
      if (areTasksLocked()) return;

      const fallback = computeFallback(i, items.length);

      clearTasks();

      setState((prev) => {
        const ready = prev.hovered !== -1 || prev.fallback === fallback;

        const debounceDuration = transitionDebounceMs;
        const debounced = hoverOutAt.current
          ? Date.now() - hoverOutAt.current.valueOf()
          : debounceDuration;

        if (
          prev.ready &&
          !ready &&
          activeIndex === -1 &&
          debounced < debounceDuration
        ) {
          setTasksLocked(true);

          setManagedTimeout(() => {
            setState((prev) => ({
              ...prev,
              ready,
            }));

            runNextFrame(() => {
              setTasksLocked(false);

              if (hoveredRef.current !== -1) {
                onHoverInside(hoveredRef.current);
              }
            });
          }, debounceDuration - debounced);

          return prev;
        }

        return prev.ready === ready
          ? prev
          : {
              ...prev,
              ready,
            };
      });

      pushTask(() =>
        setState((prev) => ({
          ...prev,
          fallback,
        }))
      );

      pushTask(() =>
        setState((prev) =>
          prev.ready
            ? prev
            : {
                ...prev,
                ready: true,
              }
        )
      );

      pushTask(() =>
        setState((prev) =>
          prev.hovered === i
            ? /* istanbul ignore next */ prev
            : {
                ...prev,
                hovered: i,
              }
        )
      );
    },
    [
      activeIndex,
      areTasksLocked,
      clearTasks,
      items.length,
      pushTask,
      runNextFrame,
      setTasksLocked,
      transitionDebounceMs,
      setManagedTimeout,
    ]
  );
  const onHoverOutside = useCallback(() => {
    hoveredRef.current = -1;
    if (areTasksLocked()) return;

    clearTasks();

    setState((prev) => {
      const fallback = computeFallback(prev.hovered, items.length);

      if (prev.hovered === -1 || fallback === prev.fallback) {
        hoverOutAt.current = new Date();
        return {
          ...prev,
          hovered: -1,
        };
      }

      pushTask(() =>
        setState((prev) => {
          hoverOutAt.current = new Date();
          return {
            ...prev,
            hovered: -1,
          };
        })
      );

      return { ...prev, fallback };
    });
  }, [areTasksLocked, clearTasks, items.length, pushTask]);

  return {
    state,
    onHoverInside,
    onHoverOutside,
  };
}
